// ---------------------------------------------------------------------------
// Sentry integration for trellis
//
// Lazy-initialized. If SENTRY_DSN is not set, all exports are no-ops.
// Works with Deno (Supabase edge functions) and Node.
// ---------------------------------------------------------------------------

let _sentry: typeof import("@sentry/deno") | null = null;
let _initialized = false;

/**
 * Initialize Sentry if SENTRY_DSN is available.
 * Safe to call multiple times — only initializes once.
 * Returns true if Sentry is active, false if skipped.
 */
export async function initSentry(): Promise<boolean> {
	if (_initialized) return _sentry !== null;
	_initialized = true;

	// @ts-ignore – Deno global
	const env = typeof Deno !== "undefined" ? Deno.env.toObject() : process?.env ?? {};
	const dsn = env.SENTRY_DSN;

	if (!dsn) return false;

	try {
		// Dynamic import — won't fail the build if @sentry/deno isn't installed
		_sentry = await import("@sentry/deno");
		_sentry.init({
			dsn,
			environment: env.SENTRY_ENVIRONMENT || env.ENVIRONMENT || "production",
			tracesSampleRate: parseFloat(env.SENTRY_TRACES_SAMPLE_RATE || "0.1"),
			// Attach server name if available
			...(env.SENTRY_SERVER_NAME ? { serverName: env.SENTRY_SERVER_NAME } : {}),
		});
		return true;
	} catch {
		// @sentry/deno not installed — silently skip
		_sentry = null;
		return false;
	}
}

/**
 * Capture an exception. No-op if Sentry is not initialized.
 */
export function captureException(
	error: unknown,
	context?: {
		route?: string;
		input?: unknown;
		auth?: { token: string | null; claims: Record<string, unknown> | null };
	},
): void {
	if (!_sentry) return;

	_sentry.withScope((scope) => {
		if (context?.route) scope.setTag("trellis.route", context.route);
		if (context?.auth?.claims?.sub) {
			scope.setUser({ id: context.auth.claims.sub as string });
		}
		if (context?.input) {
			scope.setContext("trellis", { input: context.input });
		}
		_sentry!.captureException(error);
	});
}

/**
 * Flush pending events. Call before the edge function exits.
 */
export async function flush(timeoutMs = 2000): Promise<void> {
	if (!_sentry) return;
	await _sentry.flush(timeoutMs);
}
